import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { MaskTextLoad, MaskText, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import {
  IMAGES, HERO_LINES, QUIET_MOMENTS, HERO_QUESTIONS, HERO_AFTER,
  RIPPLE, WELCOME, IMPACT, EDITIONS,
} from "@/data/content";

const HeroSplit = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} className="relative grid lg:grid-cols-[1.05fr_1fr] min-h-[100svh]" data-testid="hero-section">
      {/* Left editorial panel */}
      <div className="relative flex flex-col justify-center px-5 md:px-10 lg:pl-16 xl:pl-24 pt-32 pb-16 lg:pb-0 overflow-hidden">
        <div className="absolute -top-10 -left-16 h-[24rem] w-[24rem] rounded-full brand-gradient blur-[130px] opacity-25 animate-floaty pointer-events-none" />
        <div className="relative z-10 max-w-2xl">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 1 }}
            className="font-display text-xs md:text-sm uppercase tracking-[0.3em] text-magenta mb-6"
          >
            Where Purpose Finds a Voice
          </motion.p>

          <h1 className="font-serif font-light text-[15vw] sm:text-[11vw] lg:text-[6.2vw] leading-[0.9] tracking-tight text-ink">
            <MaskTextLoad lines={HERO_LINES} delay={0.3} stagger={0.13} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9 }}
            className="mt-8 max-w-lg font-sans text-base md:text-lg leading-relaxed text-ink/65"
          >
            In those ordinary moments, extraordinary futures are quietly being written.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/movement"
              data-testid="hero-cta-explore"
              className="group inline-flex items-center gap-2 rounded-full brand-gradient px-7 py-3.5 font-display text-xs uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(232,38,111,0.4)]"
            >
              Enter the Movement
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/register"
              data-testid="hero-cta-register"
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-7 py-3.5 font-display text-xs uppercase tracking-[0.14em] font-semibold text-ink/80 transition-all duration-300 hover:border-magenta hover:text-magenta"
            >
              Register Interest
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Right image */}
      <div className="relative min-h-[52svh] lg:min-h-full overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="A young West African woman, contemplative and confident" className="h-full w-full object-cover object-center" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/10 to-transparent hidden lg:block" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-cream to-transparent lg:hidden" />
      </div>
    </section>
  );
};

const Narrative = () => (
  <section className="relative bg-cream py-24 md:py-36 px-5 md:px-10 grain" data-testid="narrative-section">
    <div className="max-w-[1100px] mx-auto">
      <FadeUp>
        <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta mb-10">The Beginning</p>
      </FadeUp>
      <div className="space-y-8 max-w-3xl">
        {QUIET_MOMENTS.map((p, i) => (
          <FadeUp key={i} delay={0.04 * i}>
            <p className={`font-serif font-light ${i === 2 ? "text-3xl md:text-5xl leading-[1.1] text-ink" : "text-xl md:text-2xl leading-relaxed text-ink/75"}`}>
              {p}
            </p>
          </FadeUp>
        ))}
      </div>

      <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {HERO_QUESTIONS.map((q) => (
          <StaggerItem key={q} className="rounded-2xl border border-ink/10 bg-white card-shadow p-7 md:p-8">
            <span className="font-serif text-2xl md:text-3xl text-gradient">{q}</span>
          </StaggerItem>
        ))}
      </Stagger>

      <div className="mt-16 space-y-6 max-w-3xl">
        {HERO_AFTER.map((p, i) => (
          <FadeUp key={i} delay={0.03 * i}>
            <p className="font-sans text-base md:text-lg leading-relaxed text-ink/70">{p}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Ripple = () => (
  <section className="relative py-24 md:py-32 px-5 md:px-10 overflow-hidden" data-testid="ripple-section">
    <div className="max-w-[1400px] mx-auto">
      <FadeUp>
        <p className="font-sans text-lg md:text-xl text-ink/60 max-w-2xl leading-relaxed">
          And when enough young women begin living differently…
        </p>
      </FadeUp>
      <div className="mt-8 space-y-1 md:space-y-2">
        {RIPPLE.map((r, i) => (
          <h2 key={r} className="font-serif font-light text-5xl md:text-8xl leading-[0.95] text-ink">
            <MaskText lines={[r]} delay={i * 0.05} />
          </h2>
        ))}
      </div>
    </div>
  </section>
);

const Welcome = () => (
  <section className="relative overflow-hidden py-28 md:py-40 px-5 md:px-10" data-testid="welcome-section">
    <div className="absolute inset-0 brand-gradient" />
    <div className="absolute inset-0 grain" />
    <div className="relative z-10 max-w-[1200px] mx-auto text-center">
      <h2 className="font-serif font-light text-5xl md:text-8xl leading-[0.95] text-white">
        <MaskText lines={WELCOME.lines} />
      </h2>
      <FadeUp delay={0.2}>
        <p className="mt-8 font-display text-sm md:text-base uppercase tracking-[0.28em] text-white/85">
          {WELCOME.sub}
        </p>
      </FadeUp>
    </div>
  </section>
);

const Editions = () => (
  <section className="relative bg-[#1B0812] py-24 md:py-36 px-5 md:px-10 grain overflow-hidden" data-testid="editions-section">
    <div className="absolute -top-16 left-[-6rem] h-[26rem] w-[26rem] rounded-full brand-gradient blur-[150px] opacity-25 pointer-events-none" />
    <div className="relative z-10 max-w-[1400px] mx-auto">
      <FadeUp>
        <p className="font-display text-xs uppercase tracking-[0.25em] text-coral mb-6">{EDITIONS.kicker}</p>
      </FadeUp>
      <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] text-cream max-w-4xl">
        <MaskText lines={EDITIONS.title} />
      </h2>
      <FadeUp delay={0.1}>
        <p className="mt-8 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-cream/65">{EDITIONS.intro}</p>
      </FadeUp>

      <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
        {EDITIONS.countries.map((c) => {
          const isComing = c.status === "coming-soon";
          const CardInner = (
            <div className="group relative h-full min-h-[26rem] overflow-hidden rounded-3xl border border-cream/10 bg-[#12060D]">
              {c.image ? (
                <img
                  src={c.image}
                  alt={c.name}
                  className={`absolute inset-0 h-full w-full ${c.fit === "contain" ? "object-contain p-2" : "object-cover"} transition-transform duration-700 group-hover:scale-105`}
                />
              ) : (
                <div className="absolute inset-0 brand-gradient opacity-80" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1B0812] via-[#1B0812]/40 to-transparent" />
              <div className="relative z-10 flex h-full flex-col justify-end p-7 md:p-8">
                {isComing && (
                  <span className="absolute top-6 right-6 rounded-full bg-cream/15 backdrop-blur px-4 py-1.5 font-display text-[10px] uppercase tracking-[0.16em] font-bold text-cream">
                    Coming Soon
                  </span>
                )}
                <p className="font-display text-[11px] uppercase tracking-[0.2em] text-coral mb-3">{isComing ? "The next edition" : c.location}</p>
                <h3 className="font-serif font-light text-4xl md:text-5xl text-cream leading-none">{c.name}</h3>
                <p className="mt-4 font-sans text-sm md:text-base leading-relaxed text-cream/70">{c.blurb}</p>
                {c.cta && (
                  <span className="mt-6 inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.14em] font-bold text-cream group-hover:text-coral transition-colors">
                    {c.cta}
                    <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                )}
              </div>
            </div>
          );
          return (
            <StaggerItem key={c.id} className="h-full">
              {c.link ? (
                <Link
                  to={c.link}
                  data-testid={`edition-card-${c.id}`}
                  className="block h-full"
                >
                  {CardInner}
                </Link>
              ) : (
                <div data-testid={`edition-card-${c.id}`} className="h-full cursor-default">{CardInner}</div>
              )}
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

const Impact = () => (
  <section className="relative bg-cream py-24 md:py-36 px-5 md:px-10" data-testid="impact-section">
    <div className="max-w-[1400px] mx-auto">
      <FadeUp>
        <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta mb-6">Our Impact</p>
      </FadeUp>
      <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] text-ink max-w-4xl">
        <MaskText lines={["Some count attendance.", "We count possibilities."]} />
      </h2>
      <FadeUp delay={0.1}>
        <p className="mt-8 max-w-2xl font-sans text-base md:text-lg leading-relaxed text-ink/70">{IMPACT.lead[0]}</p>
      </FadeUp>

      <div className="mt-14 grid md:grid-cols-3 gap-4">
        {IMPACT.stats.map((s, i) => (
          <FadeUp key={i} delay={0.08 * i}>
            <div className="rounded-3xl border border-ink/10 bg-white card-shadow p-8 h-full">
              <span className="font-serif text-7xl md:text-8xl text-gradient leading-none">{s.value}</span>
              <p className="mt-4 font-sans text-lg text-ink/70">{s.label}</p>
            </div>
          </FadeUp>
        ))}
      </div>

      <Stagger className="mt-12 flex flex-wrap gap-3">
        {IMPACT.outcomes.map((o) => (
          <StaggerItem key={o}>
            <span className="inline-block rounded-full border border-ink/15 px-5 py-2.5 font-display text-xs md:text-sm uppercase tracking-[0.14em] text-ink/75">
              {o}
            </span>
          </StaggerItem>
        ))}
      </Stagger>

      <FadeUp delay={0.15}>
        <p className="mt-14 font-serif italic font-light text-2xl md:text-4xl leading-snug text-ink max-w-3xl">
          {IMPACT.close}
        </p>
      </FadeUp>

      <FadeUp delay={0.2}>
        <Link
          to="/voices"
          data-testid="impact-readmore"
          className="mt-10 group inline-flex items-center gap-3 font-display text-sm uppercase tracking-[0.16em] text-ink hover:text-magenta transition-colors"
        >
          Read the words for parents, schools & young women
          <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="bg-cream">
      <HeroSplit />
      <div className="py-8 md:py-10 bg-cream border-y border-ink/10">
        <Marquee text="Believe. Become. Lead." variant="dark" animClass="animate-marquee" />
      </div>
      <Narrative />
      <Ripple />
      <Welcome />
      <Editions />
      <Impact />
    </div>
  );
}
